/**
 * Server notifications for the header bell — GET / PATCH /v1/notifications (OPEN-085 follow-up).
 *
 * The API returns UNREAD rows addressed to the caller's role or any of its combined-role
 * components, newest first, with no paging. Every office role gets 200 (PLATFORM_ADMIN simply
 * gets an empty list: nothing writes to it today). relatedEntityType is 'job' (the id is the job
 * id) or 'CATALOG_CHANGE' (a catalog change id; the BILLING_MANAGER "review open invoices"
 * notice from catalog_change_apply_approval). Each app decides where a row links to.
 *
 * Read state lives on the ROW and is shared by the whole role: marking one read clears it for
 * every user holding that role. (The InReview escalation banner no longer reads these rows: it
 * counts tickets carrying the ESCALATED flag now — OPEN-152.) ALL read logic for the bell is in this module — the query, the unread count and
 * the mark-read mutation — so per-user read state can be swapped in here without touching the
 * component. Moved here from web-dispatch so web-billing's bell shares it (OPEN-085 follow-up).
 */

import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { authedFetch } from '../auth/api-client.js';
import { useApiOpts } from '../api/api-opts-context.js';
import { apiErrorFromResponse } from '../api/api-error.js';
import { useApiQuery } from '../api/use-api-query.js';

/** One row of GET /v1/notifications (Task 24.9 table). */
export interface InAppNotification {
  readonly id: string;
  readonly message: string;
  readonly isRead: boolean;
  readonly relatedEntityType: string | null;
  readonly relatedEntityId: string | null;
  readonly createdAt: string;
}

interface NotificationsResponse {
  readonly notifications: InAppNotification[];
}

/** Prefix shared by every notifications cache (this one and the InReview banner's). */
const NOTIFICATIONS_KEY = ['notifications'] as const;
const BELL_KEY = ['notifications', 'bell'] as const;

/** Shown in the drawer, because a read is not private to the reader. */
export const SHARED_READ_NOTE = 'Marking a notification read clears it for everyone in your role.';

export function useServerNotifications(): UseQueryResult<NotificationsResponse> {
  const apiOpts = useApiOpts();
  return useApiQuery<NotificationsResponse>(apiOpts, '/v1/notifications', {
    queryKey: [...BELL_KEY],
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

/** Unread rows only — the API returns no read rows today, but don't depend on it. */
export function unreadServerNotifications(
  rows: readonly InAppNotification[] | undefined,
): InAppNotification[] {
  return (rows ?? []).filter((n) => !n.isRead);
}

/** The bell's badge: browser-local rejection notices plus unread server rows. */
export function totalUnread(
  localUnread: number,
  rows: readonly InAppNotification[] | undefined,
): number {
  return localUnread + unreadServerNotifications(rows).length;
}

/**
 * Mark one row read. The row leaves the bell's cache at once so the badge drops without waiting
 * for the PATCH; every notifications cache is refetched afterwards (a 404 means someone in the
 * role already read it, and the refetch settles that too).
 */
export function useMarkNotificationRead(): UseMutationResult<
  { ok: true },
  Error,
  string,
  { previous: NotificationsResponse | undefined }
> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useMutation<{ ok: true }, Error, string, { previous: NotificationsResponse | undefined }>({
    mutationFn: async (id: string): Promise<{ ok: true }> => {
      // DEC-201 — the route takes no fields, but a body-less PATCH 500s; send `{}`.
      const res = await authedFetch(opts, `/v1/notifications/${id}/read`, {
        method: 'PATCH',
        body: '{}',
      });
      if (!res.ok && res.status !== 404) {
        throw await apiErrorFromResponse(res, 'notification read');
      }
      return { ok: true };
    },
    onMutate: (id: string) => {
      const previous = qc.getQueryData<NotificationsResponse>([...BELL_KEY]);
      if (previous !== undefined) {
        qc.setQueryData<NotificationsResponse>([...BELL_KEY], {
          ...previous,
          notifications: previous.notifications.filter((n) => n.id !== id),
        });
      }
      void qc.cancelQueries({ queryKey: [...BELL_KEY] });
      return { previous };
    },
    onError: (_err, _id, ctx): void => {
      if (ctx?.previous !== undefined) qc.setQueryData([...BELL_KEY], ctx.previous);
    },
    onSettled: (): void => {
      void qc.invalidateQueries({ queryKey: [...NOTIFICATIONS_KEY] });
    },
  });
}
